import React, { useState, useEffect } from 'react';
import { setupLocalDevelopmentEnvironment } from '../../utils/localDevelopmentHelper';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import ErrorPanel from '../../components/common/ErrorPanel';
import RestCallTestPanel from '../../components/admin/testTools/RestCallTestPanel';
import RawApiRequest from '../../components/admin/testTools/RawApiRequest';
import TenantSelector from '../../components/admin/TenantSelector';
import { getTenants } from '../../api/backendApi';
import { Tabs, Tab, Box } from '@mui/material';

/**
 * REST Test Tools Page
 * Provides utilities to test REST API calls to Algo devices
 */
const RestTestToolsPage = () => {
  const { currentUser, isAdmin, claims } = useAuth();
  const [refreshTenant, setRefreshTenant] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);

  // Only allow admin users to access this page
  if (!currentUser || !isAdmin) {
    return (
      <MainLayout>
        <div className="p-6 max-w-4xl mx-auto bg-red-50 border border-red-200 rounded-md text-center">
          <h1 className="text-xl font-bold text-red-700 mb-2">Access Denied</h1>
          <p className="text-red-600">This page is only accessible to administrators.</p>
        </div>
      </MainLayout>
    );
  }

  // Check for locally stored tenant or get tenant from claims
  useEffect(() => {
    // Set up mock tenant for local development
    setupLocalDevelopmentEnvironment();

    const storedTenant = localStorage.getItem('selectedRestToolsTenant');

    if (storedTenant) {
      setSelectedTenant(JSON.parse(storedTenant));
    }

    setLoading(false);
  }, []);

  // Handle tenant selection
  const handleTenantSelected = tenantId => {
    // Find tenant details by ID (if needed for display)
    getTenants()
      .then(tenants => {
        const tenant = tenants.find(t => t.id === tenantId);
        if (tenant) {
          setSelectedTenant(tenant);
          localStorage.setItem('selectedRestToolsTenant', JSON.stringify(tenant));
        }
      })
      .catch(err => {
        console.error('Error fetching tenant details:', err);
      });
  };

  // Show loading state
  if (loading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-6">REST Tools</h1>
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Check for tenant context
  const tenantId = selectedTenant?.id || claims?.tenantId || currentUser?.tenantId;
  if (!tenantId) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-6">REST Tools</h1>
          <TenantSelector onTenantSelected={handleTenantSelected} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <Toaster position="top-right" />
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">REST Tools</h1>
          <div className="flex items-center gap-4">
            {selectedTenant && (
              <div className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-200">
                Tenant: {selectedTenant.name}
              </div>
            )}
            {selectedTenant && (
              <button
                onClick={() => {
                  localStorage.removeItem('selectedRestToolsTenant');
                  setSelectedTenant(null);
                }}
                className="text-sm text-gray-600 hover:text-red-600"
              >
                Change Tenant
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            aria-label="REST Test Tools Tabs"
          >
            <Tab label="Device REST Calls" />
            <Tab label="Raw API Request" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <div className="grid grid-cols-1 gap-6">
          {selectedTab === 0 && <RestCallTestPanel tenantId={tenantId} />}
          {selectedTab === 1 && <RawApiRequest />}
        </div>
      </div>
    </MainLayout>
  );
};

export default RestTestToolsPage;
